import { useState, useRef, DragEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import UploadEventsModal from '../../components/UploadEventsModal';
import { vendorApi } from '../../services/vendor';
import type { SiteListItem, UploadListItem } from '../../types';
import extractApiError from '../../utils/extractApiError';

const statusVariant = (s: string) => {
  switch (s) {
    case 'completed': return 'success' as const;
    case 'processing': return 'warning' as const;
    case 'queued': return 'info' as const;
    case 'failed': return 'error' as const;
    case 'partial': return 'info' as const;
    default: return 'neutral' as const;
  }
};

// Poll every 3s while any upload is mid-flight; stop once everything settles.
// Shared between the main uploads list and the stats-strip queries so the
// definition of "in flight" stays in one place.
const POLL_INTERVAL_MS = 3000;
const isInFlight = (s: string) => s === 'queued' || s === 'processing';
const pollWhileInFlight = (query: { state: { data?: { results?: { status: string }[] } } }) => {
  const results = query.state.data?.results ?? [];
  return results.some((u) => isInFlight(u.status)) ? POLL_INTERVAL_MS : false;
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const columns: Column<UploadListItem>[] = [
  { key: 'file_name', header: 'File Name', sortable: true },
  { key: 'file_type', header: 'Type', sortable: true,
    render: (row) => <span className="uppercase text-xs font-medium text-slate-500">{row.file_type}</span>,
  },
  { key: 'site_name', header: 'Site', sortable: true },
  { key: 'file_size', header: 'Size',
    render: (row) => <span className="text-slate-500">{formatFileSize(row.file_size)}</span>,
  },
  { key: 'status', header: 'Status', sortable: true,
    render: (row) => <StatusBadge label={row.status} variant={statusVariant(row.status)} />,
  },
  { key: 'events_parsed_count', header: 'Events', sortable: true,
    render: (row) => <span className="text-slate-500">{row.events_parsed_count}</span>,
  },
  { key: 'created_at', header: 'Uploaded', sortable: true,
    render: (row) => <span className="text-slate-500">{new Date(row.created_at).toLocaleString()}</span>,
  },
  {
    key: 'download_url',
    header: 'File',
    render: (r) => r.download_url ? (
      <a
        href={r.download_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-blue-600 hover:text-blue-700 text-xs font-medium"
      >
        Download
      </a>
    ) : <span className="text-xs text-slate-400">&mdash;</span>,
  },
];

export default function VendorUploads() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [page, setPage] = useState(1);
  const [siteFilter, setSiteFilter] = useState('');
  const [rulesOpen, setRulesOpen] = useState(false);
  const [openEventsUpload, setOpenEventsUpload] = useState<UploadListItem | null>(null);

  // Load sites for the dropdown (only active ones assigned to this vendor)
  const { data: sitesData } = useQuery({
    queryKey: ['vendor', 'sites', 'all'],
    queryFn: () => vendorApi.getSites({ vendor_assignment_status: 'active', page_size: '100' }).then((r) => r.data),
  });
  const sites: SiteListItem[] = sitesData?.results ?? [];

  // Load upload history.  Uploads are now processed in a background
  // thread on the server, so we poll while any row is still in flight
  // (queued/processing) and stop polling once everything settles.
  const uploadParams: Record<string, string> = { page: String(page) };
  if (siteFilter) uploadParams.site = siteFilter;
  const { data: uploadsData, isLoading: uploadsLoading } = useQuery({
    queryKey: ['vendor', 'uploads', page, siteFilter],
    queryFn: () => vendorApi.getUploads(uploadParams).then((r) => r.data),
    refetchInterval: pollWhileInFlight,
  });
  const totalPages = uploadsData ? Math.ceil(uploadsData.count / 20) : 1;

  // Compute stats from all uploads (page 1 without filter)
  const { data: allUploadsData } = useQuery({
    queryKey: ['vendor', 'uploads', 'stats'],
    queryFn: () => vendorApi.getUploads({ page: '1', page_size: '100' }).then((r) => r.data),
    refetchInterval: pollWhileInFlight,
  });

  const uploadStats = {
    total: allUploadsData?.count ?? 0,
    processed: allUploadsData?.results?.filter((u) => u.status === 'completed').length ?? 0,
    processing: allUploadsData?.results?.filter((u) => isInFlight(u.status)).length ?? 0,
    failed: allUploadsData?.results?.filter((u) => u.status === 'failed').length ?? 0,
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 6000);
  };

  const uploadMutation = useMutation({
    mutationFn: ({ file, siteId }: { file: File; siteId: string }) =>
      vendorApi.uploadFile(file, siteId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['vendor', 'uploads'] });
      showToast(
        `"${res.data.file_name}" queued for parsing. This usually takes 30–90 seconds — the row below will update automatically when it's done.`,
        'success',
      );
    },
    onError: (err: unknown) => {
      showToast(extractApiError(err), 'error');
    },
  });

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!selectedSiteId) {
      showToast('Please select a site before uploading.', 'error');
      return;
    }
    for (let i = 0; i < files.length; i++) {
      uploadMutation.mutate({ file: files[i], siteId: selectedSiteId });
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!selectedSiteId) return;
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <>
      <PageHeader
        title="Uploads"
        description="Upload Daily Activity Reports (DARs) for your sites"
      />

      {/* Toast */}
      {toast && (
        <div className={`mb-4 rounded-xl border px-4 py-3 ${
          toast.type === 'success'
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-red-200 bg-red-50'
        }`}>
          <p className={`text-sm ${toast.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
            {toast.message}
          </p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Uploads', value: uploadStats.total, color: 'text-slate-900' },
          { label: 'Processed', value: uploadStats.processed, color: 'text-emerald-600' },
          { label: 'Processing', value: uploadStats.processing, color: 'text-amber-600' },
          { label: 'Failed', value: uploadStats.failed, color: 'text-red-600' },
        ].map((stat) => (
          <div key={stat.label} className="rounded-2xl border border-slate-100 bg-white p-4">
            <p className="text-xs font-medium text-slate-400 uppercase">{stat.label}</p>
            <p className={`mt-1 text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Upload Section */}
      <div className="mb-8 rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-sm font-semibold text-slate-900 mb-4">Upload a Report</h2>

        {/* Site selector */}
        <div className="mb-4">
          <label htmlFor="upload-site" className="block text-xs font-medium text-slate-500 mb-1">
            Select Site
          </label>
          <select
            id="upload-site"
            value={selectedSiteId}
            onChange={(e) => setSelectedSiteId(e.target.value)}
            className="w-full max-w-md rounded-xl border-2 border-slate-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">Choose a site...</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {sites.length === 0 && (
            <p className="mt-1 text-xs text-slate-400">No active sites. Sites must be accepted before you can upload.</p>
          )}
        </div>

        {/* Drag and drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => selectedSiteId && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectedSiteId && fileInputRef.current?.click();
            }
          }}
          className={`relative rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            !selectedSiteId
              ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'
              : isDragging
                ? 'border-brand-400 bg-brand-50'
                : 'border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-brand-50 cursor-pointer'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.csv,.xlsx,.xls,.txt,.docx,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />

          {uploadMutation.isPending ? (
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8 text-brand-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-sm font-medium text-brand-600">Uploading...</p>
            </div>
          ) : (
            <>
              <svg className="mx-auto h-10 w-10 text-slate-400 mb-3" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <p className="text-sm font-medium text-slate-700">
                {selectedSiteId ? 'Drag and drop files here, or click to browse' : 'Select a site above to upload'}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                PDF, CSV, Excel, Word, TXT, PNG, JPG — Max 50 MB
              </p>
            </>
          )}
        </div>
      </div>

      {/* File Processing Rules (collapsible) */}
      <div className="mb-8 rounded-2xl border border-slate-100 bg-white">
        <button
          onClick={() => setRulesOpen(!rulesOpen)}
          className="w-full flex items-center justify-between px-6 py-4 text-left"
        >
          <span className="text-sm font-semibold text-slate-900">File Processing Rules</span>
          <svg className={`w-5 h-5 text-slate-400 transition-transform ${rulesOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        {rulesOpen && (
          <div className="px-6 pb-5 text-sm text-slate-600 space-y-2 border-t border-slate-100 pt-4">
            <p><strong>Supported formats:</strong> PDF, CSV, Excel (.xlsx/.xls), Word (.docx), plain text (.txt), images (PNG, JPG)</p>
            <p><strong>Max file size:</strong> 50 MB per file</p>
            <p><strong>AI parsing:</strong> Files are parsed with GPT-4o to extract structured events (patrols, incidents, admin tasks)</p>
            <p><strong>Guard matching:</strong> Guard names in reports are automatically matched to registered guards</p>
            <p><strong>Processing time:</strong> Parsing runs in the background — usually 30–90 seconds. You can navigate away and you'll get a notification when it's done.</p>
            <p><strong>Site selection:</strong> Each upload must be associated with a site for proper scoring</p>
          </div>
        )}
      </div>

      {/* Upload History */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Upload History</h2>
        <div className="flex gap-3">
          <select
            value={siteFilter}
            onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
            className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All Sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      <DataTable<UploadListItem>
        columns={columns}
        data={uploadsData?.results ?? []}
        keyExtractor={(row) => row.id}
        isLoading={uploadsLoading}
        emptyTitle="No uploads yet"
        emptyDescription="Upload a DAR file above to get started."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={(row) => setOpenEventsUpload(row)}
      />

      <UploadEventsModal
        upload={openEventsUpload}
        onClose={() => setOpenEventsUpload(null)}
      />
    </>
  );
}
